import Form, {
  ButtonItem,
  ButtonOptions,
  GroupItem,
  Label,
  SimpleItem,
} from 'devextreme-react/form';
import { RequiredRule } from 'devextreme-react/validator';
import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import useSWR from 'swr';
import { useLoading } from '../../contexts/loadingContext';
import { useTranslation } from '../../contexts/translation';
import { useSafeGoBack } from '../../hooks/useSafeGoBack';
import useUnsavedChangesWarning from '../../hooks/useUnsavedChangesWarning';
import { IForm } from '../../types/iForm';
import { API_URL, fetcher } from '../../utils/apiUrl';
import { formTranslationByEdit } from '../../utils/formTranslationByEdit';

interface Props extends IForm {
  /** Entita jde nastavit jen při zakládání - na BE je součástí unikátního klíče */
  disableEntity?: boolean;
}

export const AttachmentCategoriesForm = ({ onSubmit, formData, isEdit, disableEntity }: Props) => {
  const { translate } = useTranslation();
  const transFormName = formTranslationByEdit('AttachmentCategory', isEdit);
  const history = useHistory();
  const backRoute = '/attachment-categories';
  const formRef = useRef<Form>(null);
  const { loading } = useLoading();
  const safeGoBack = useSafeGoBack('/home');

  // entity a role bereme z API, ať se v číselníku neobjeví překlep, který nikde nevyskočí
  const { data: entities } = useSWR<string[]>(API_URL.ATTACHMENT_SUPPORTED_ENTITIES, fetcher);
  const { data: roles } = useSWR<string[]>(API_URL.USER_SUPPORTED_ROLES, fetcher);

  const [prompt, setDirty, setClean] = useUnsavedChangesWarning(backRoute, () => {
    if (!formRef) return;
    if (formRef.current!.instance.validate().isValid) {
      setClean();
      onSubmit(null).then((result) => {
        if (result) history.push(backRoute);
        else setDirty();
      });
    }
  });

  return (
    <form
      onSubmit={(e) => {
        onSubmit(e);
        if (formRef && formRef.current && !isEdit) formRef.current?.instance.resetValues();
        // @ts-ignore
        setClean();
      }}
    >
      <Form
        colCount={1}
        formData={formData}
        showValidationSummary={true}
        onFieldDataChanged={() => {
          // @ts-ignore
          setDirty();
        }}
        ref={formRef}
      >
        <GroupItem>
          <SimpleItem
            dataField="entity"
            editorType="dxSelectBox"
            editorOptions={{
              items: entities ?? [],
              disabled: disableEntity,
            }}
          >
            <Label text={translate!('Entity', transFormName)} />
            <RequiredRule message={translate!('Entity is required', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem dataField="name" editorType="dxTextBox" editorOptions={{ maxLength: 100 }}>
            <Label text={translate!('Name', transFormName)} />
            <RequiredRule message={translate!('Enter name', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem
            dataField="requiredRole"
            editorType="dxTagBox"
            editorOptions={{
              items: roles ?? [],
              acceptCustomValue: true,
              showClearButton: true,
              // na BE se role ukládají jako jeden řetězec oddělený středníkem
              value:
                formData && formData['requiredRole'] ? formData['requiredRole'].split(';') : [],
              onValueChanged: (e: any) => {
                if (formData) formData['requiredRole'] = e.value?.length ? e.value.join(';') : null;
              },
            }}
          >
            <Label text={translate!('Required role', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem dataField="sortOrder" editorType="dxNumberBox">
            <Label text={translate!('Sort order', transFormName)} />
          </SimpleItem>
        </GroupItem>
        <GroupItem>
          <SimpleItem dataField="visible" editorType="dxCheckBox">
            <Label text={translate!('Visible', transFormName)} />
          </SimpleItem>
        </GroupItem>

        <GroupItem colCount={14}>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Close', 'Asset.Update')}
              type={'normal'}
              useSubmitBehavior={false}
              onClick={() => safeGoBack()}
            />
          </ButtonItem>
          <ButtonItem colSpan={1} verticalAlignment={'center'} horizontalAlignment={'left'}>
            <ButtonOptions
              width={'100%'}
              text={translate!('Save', transFormName)}
              type={'success'}
              disabled={loading}
              useSubmitBehavior
            />
          </ButtonItem>
        </GroupItem>
        {prompt}
      </Form>
    </form>
  );
};
